import { theme as antdTheme } from 'antd';

import { PANEL_STICKY_HEADER_DEFAULT } from '../hooks/usePanelStickyOffset';
import {
  BRAND_PRIMARY,
  BRAND_PRIMARY_DARK,
  BRAND_PRIMARY_LIGHT,
  DANGER,
  DARK_SURFACES,
  LIGHT_SURFACES,
  LIGHT_TEXT,
  brandAlpha,
} from './brandColors';

const sharedToken = {
  colorPrimary: BRAND_PRIMARY,
  colorInfo: BRAND_PRIMARY,
  colorLink: BRAND_PRIMARY,
  colorSuccess: '#10b981',
  colorWarning: '#f59e0b',
  colorError: DANGER.base,
  /** متن روی دکمه‌های پررنگ همیشه سفید — teal برند نباید متن تیره شود */
  colorTextLightSolid: '#ffffff',

  borderRadius: 10,
  borderRadiusLG: 14,
  borderRadiusSM: 8,

  fontFamily: 'var(--app-font-family)',
  fontSize: 14,

  controlHeight: 36,
  controlHeightLG: 40,
  controlHeightSM: 28,

  wireframe: false,
  motionDurationMid: '0.25s',
};

const lightToken = {
  ...sharedToken,
  colorBgBase: LIGHT_SURFACES.surface,
  colorBgLayout: LIGHT_SURFACES.bg,
  colorBgContainer: LIGHT_SURFACES.surface,
  colorBgElevated: LIGHT_SURFACES.elevated,
  colorBorder: LIGHT_SURFACES.border,
  colorBorderSecondary: LIGHT_SURFACES.borderSoft,
  colorTextBase: LIGHT_TEXT.primary,
  colorTextSecondary: LIGHT_TEXT.secondary,
  colorTextTertiary: LIGHT_TEXT.muted,
  boxShadowTertiary: '0 4px 24px -8px rgba(7, 11, 20, 0.07)',
};

const darkToken = {
  ...sharedToken,
  colorPrimary: BRAND_PRIMARY_LIGHT,
  colorInfo: BRAND_PRIMARY_LIGHT,
  colorLink: BRAND_PRIMARY_LIGHT,
  colorBgBase: DARK_SURFACES.bg,
  colorBgLayout: DARK_SURFACES.bg,
  colorBgContainer: DARK_SURFACES.surface,
  colorBgElevated: DARK_SURFACES.elevated,
  colorBorder: DARK_SURFACES.border,
  colorBorderSecondary: DARK_SURFACES.borderSoft,
  colorTextBase: '#f0f4f3',
  colorTextSecondary: '#c5d4d0',
  colorTextTertiary: '#8fa8a4',
};

const buildComponents = (isDark) => {
  const primary = isDark ? BRAND_PRIMARY_LIGHT : BRAND_PRIMARY;
  const hoverBg = brandAlpha(isDark ? 0.16 : 0.1);
  const selectedBg = brandAlpha(isDark ? 0.22 : 0.14);
  const primaryShadow = `0 6px 20px -8px ${brandAlpha(0.55)}`;
  const focusRing = `0 0 0 3px ${brandAlpha(0.18)}`;

  return {
    Layout: {
      headerBg: 'transparent',
      headerHeight: PANEL_STICKY_HEADER_DEFAULT,
      headerPadding: '0 24px',
      bodyBg: 'transparent',
      siderBg: 'transparent',
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: isDark ? '#c5d4d0' : LIGHT_TEXT.secondary,
      itemHoverBg: hoverBg,
      itemHoverColor: primary,
      itemActiveBg: selectedBg,
      itemSelectedBg: selectedBg,
      itemSelectedColor: isDark ? '#ffffff' : BRAND_PRIMARY_DARK,
      subMenuItemBorderRadius: 10,
      itemBorderRadius: 10,
      itemMarginInline: 8,
      itemMarginBlock: 2,
      itemHeight: 38,
      iconSize: 18,
      iconMarginInlineEnd: 12,
      groupTitleColor: isDark ? '#8fa8a4' : LIGHT_TEXT.muted,
      groupTitleFontSize: 11,
      popupBg: isDark ? DARK_SURFACES.elevated : LIGHT_SURFACES.surface,
    },
    Card: {
      borderRadiusLG: 14,
      paddingLG: 16,
    },
    Button: {
      fontWeight: 600,
      primaryShadow,
      primaryColor: '#ffffff',
      defaultHoverColor: primary,
      defaultHoverBorderColor: primary,
    },
    Table: {
      /* مات — transparent باعث دیده شدن متن ردیف‌ها از زیر هدر sticky می‌شد */
      headerBg: isDark ? DARK_SURFACES.surface2 : LIGHT_SURFACES.surface2,
      headerColor: isDark ? '#c5d4d0' : LIGHT_TEXT.secondary,
      headerSplitColor: 'transparent',
      rowHoverBg: brandAlpha(isDark ? 0.08 : 0.05),
      borderColor: isDark ? DARK_SURFACES.border : LIGHT_SURFACES.borderSoft,
    },
    Input: {
      activeShadow: focusRing,
      hoverBorderColor: primary,
    },
    Select: {
      optionSelectedBg: selectedBg,
      optionActiveBg: hoverBg,
    },
    Tag: {
      borderRadiusSM: 999,
    },
    Tabs: {
      titleFontSize: 14,
      inkBarColor: primary,
      itemSelectedColor: primary,
      itemHoverColor: primary,
    },
    Switch: {
      colorPrimary: BRAND_PRIMARY,
      colorPrimaryHover: BRAND_PRIMARY_DARK,
    },
    Pagination: {
      itemActiveBg: selectedBg,
    },
    Segmented: {
      itemSelectedBg: isDark ? DARK_SURFACES.elevated : '#ffffff',
      itemSelectedColor: primary,
    },
  };
};

export const buildAntdTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: isDark ? darkToken : lightToken,
    components: buildComponents(isDark),
    cssVar: { prefix: 'mp' },
    hashed: true,
  };
};
